import { Injectable, Scopes } from '@visiba/instantiation';
import { Signal, signal } from '@preact/signals-react';
import { EventEmitter } from '@visiba/std';
import { applicationLocalStorage } from '@application/application_storage_local';
import { DateService } from '@application/time/date.service';
import { Logger } from '@visiba/logger';

@Injectable({
	scope: Scopes.Singleton,
})
export class ConsentService extends EventEmitter<{
	onConsent: Revision;
}> {
	private readonly logger = new Logger('ConsentService');

	public hasRevision: Signal<Nullable<Revision>>;

	private consent: number;

	constructor(private readonly dateService: DateService) {
		super();

		const revision = this.parseRevision().ok;
		this.consent = revision?.consent ?? 0;

		this.hasRevision = signal(revision);
	}

	public setDefault(...consents: UserConsent[]) {
		// If the value already have a value, do nothing.

		if (this.consent !== 0) return;

		this.giveConsent(...consents);
	}

	public giveConsent(...consents: UserConsent[]) {
		// Set consent using bitwise OR
		this.consent = UserConsent.Essential;

		for (const consent of consents) {
			this.consent |= consent;
		}

		this.logger.logVerbose('Users consent level is at {consent}', this.consent);
	}

	public hasConsent(consent: UserConsent): boolean {
		// Check consent using bitwise AND
		return (this.consent & consent) !== 0;
	}

	public revokeConsent(consent: UserConsent): void {
		// Remove consent using bitwise AND with negation
		this.consent &= ~consent;
	}

	public getConsent(): number {
		return this.consent;
	}

	public acceptConsent(...consents: UserConsent[]) {
		this.giveConsent(...consents);

		const revision = {
			version: 0,
			timestamp: this.dateService.newDate().toISOString(),
			consent: this.getConsent(),
		} satisfies Revision;

		applicationLocalStorage.set('cookieConsentRevision', revision);

		this.hasRevision.value = revision;

		this.emit('onConsent', revision);
	}

	private parseRevision(): Result<Revision> {
		const consent = applicationLocalStorage.get('cookieConsentRevision');

		if (consent == null) {
			return Err(new Error('No consent found'));
		}

		if (consent?.consent == null || consent?.timestamp == null || consent?.version == null) {
			return Err(new Error('Consent data has malfunction'));
		}

		return Ok(consent);
	}
}

// prettier-ignore
export enum UserConsent {
	Essential  = 1 << 0,
	Analytic   = 1 << 1,
	Functional = 1 << 2,
}

type Revision = {
	version: number;
	timestamp: string;
	consent: number;
};
